import React, { useCallback, useMemo, useRef } from "react";
import { syntaxHighlightJsonText } from "../../helpers/json";
import styles from "./JsonEditor.module.css";

export interface JsonEditorProps {
  value: string;
  onChange: (value: string) => void;
  /** Display-only mode (remote configs): highlighting stays, edits are refused. */
  readOnly?: boolean;
  style?: React.CSSProperties;
  className?: string;
}

/**
 * Plain-text JSON editor with syntax highlighting: a transparent textarea is
 * overlaid on a `<pre>` holding the colored tokens, both kept scroll-synced.
 */
const JsonEditor = ({ value, onChange, readOnly, style, className }: JsonEditorProps) => {
  const preRef = useRef<HTMLPreElement>(null);

  // Trailing newline keeps the last (possibly empty) line visible in the <pre>,
  // so the highlight layer never gets shorter than the textarea content.
  const html = useMemo(() => syntaxHighlightJsonText(value) + "\n", [value]);

  const syncScroll = useCallback((e: React.UIEvent<HTMLTextAreaElement>) => {
    const pre = preRef.current;
    if (!pre) return;
    pre.scrollTop = e.currentTarget.scrollTop;
    pre.scrollLeft = e.currentTarget.scrollLeft;
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (readOnly || e.key !== "Tab") return;
    e.preventDefault();
    const target = e.currentTarget;
    const { selectionStart, selectionEnd } = target;
    const next = value.slice(0, selectionStart) + "  " + value.slice(selectionEnd);
    onChange(next);
    requestAnimationFrame(() => {
      target.selectionStart = target.selectionEnd = selectionStart + 2;
    });
  }, [value, onChange, readOnly]);

  return (
    <div className={`${styles.editor}${className ? ` ${className}` : ""}`} style={style}>
      <pre ref={preRef} aria-hidden className={styles.highlight} dangerouslySetInnerHTML={{ __html: html }} />
      <textarea
        className={styles.input}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        readOnly={readOnly}
        onScroll={syncScroll}
        onKeyDown={handleKeyDown}
        spellCheck={false}
        autoCapitalize="off"
        autoCorrect="off"
        wrap="off"
      />
    </div>
  );
};

export default JsonEditor;
